import {
  ActionViewModel,
  durationInSecondsOrZero,
  secondsToDuration,
} from "./activities/view-models/ActionViewModel";
import { AutonomousActivityType } from "protos/atk/enums";
import { effectorFinalActionIndexes } from "./activities/view-models/utils";
import {
  QuickActionStage,
  RigActivitiesConfiguration,
  RigActivityConfig,
  RigActivityConstraints,
} from "protos/atk/activities";
import { quickActionToViewModel } from "./activities/view-models/factory";
import { RigEffectiveConfig } from "protos/atk/effective_config";

export interface RigActivityConfigViewModel {
  activity: AutonomousActivityType;
  present: boolean;
  constraints: RigActivityConstraints;
  actions: ActionViewModel<unknown>[];
  nStages: number;
  quickActionTimeSec: number;
}

export type StructuredActionsViewModel = {
  actionIndexesRenderingInStageIndex: Map<number, number[]>;
  effectorFinalActionIndexes: Set<number>;
};

export interface RigActivitiesFormViewModel {
  rigEffectiveConfig: RigEffectiveConfig;
  activities: {
    [activity in AutonomousActivityType]?: RigActivityConfigViewModel;
  };
}

function actionsConfigToViewModel(quickActionStages: QuickActionStage[]): {
  actions: ActionViewModel<unknown>[];
  nStages: number;
} {
  const actions: ActionViewModel<unknown>[] = [];

  // convert all the quick actions
  for (
    let stageIndex = 0;
    stageIndex < quickActionStages.length;
    stageIndex++
  ) {
    for (const quickAction of quickActionStages[stageIndex]?.actions ?? []) {
      const viewModel = quickActionToViewModel(quickAction, stageIndex);

      if (viewModel) {
        actions.push(viewModel);
      }
    }
  }

  return {
    actions,
    nStages: quickActionStages.length,
  };
}

function actionsViewModelToConfig(
  vmActions: ActionViewModel<unknown>[]
): QuickActionStage[] {
  const quickActionStages: QuickActionStage[] = [];

  // we're hoping to get a compact array here and all the ways of doing
  // anything with number-valued keys in an object suck so use an array instead
  const actionIndexesByStartStageIndex: (number[] | undefined)[] = [];

  for (let actionIndex = 0; actionIndex < vmActions.length; actionIndex++) {
    const action = vmActions[actionIndex]!;
    const actionIndexesThisStage =
      actionIndexesByStartStageIndex[action.startInStageIndex] ?? [];

    actionIndexesThisStage.push(actionIndex);
    actionIndexesByStartStageIndex[action.startInStageIndex] =
      actionIndexesThisStage;
  }

  const efai = effectorFinalActionIndexes(vmActions);

  for (const vmStage of actionIndexesByStartStageIndex) {
    const qaStage: QuickActionStage = { actions: [] };

    for (const vmActionIndex of vmStage ?? []) {
      const vmAction = vmActions[vmActionIndex]!;
      const quickAction = vmAction.toQuickAction(efai.has(vmActionIndex));
      qaStage.actions.push(quickAction);
    }

    quickActionStages.push(qaStage);
  }

  return quickActionStages;
}

function defaultRigActivityConfigViewModel(
  activity: AutonomousActivityType
): RigActivityConfigViewModel {
  return {
    activity,
    present: false,
    constraints: {
      maxHitchAngleRad: 0,
      minTurnRadiusM: 0,
      workingSpeedMps: 0,
      turningSpeedMps: 0,
      transitionSpeedMps: 0,
    },
    quickActionTimeSec: 0,
    actions: [],
    nStages: 0,
  };
}

function rigActivityConfigToViewModel(
  config: RigActivityConfig
): RigActivityConfigViewModel {
  const { actions, nStages } = actionsConfigToViewModel(
    config.quickActionStages
  );

  return {
    activity: config.activity,
    present: true,
    constraints: config.constraints ?? {
      maxHitchAngleRad: 0,
      minTurnRadiusM: 0,
      workingSpeedMps: 0,
      turningSpeedMps: 0,
      transitionSpeedMps: 0,
    },
    quickActionTimeSec: durationInSecondsOrZero(config.quickActionTime),
    actions,
    nStages,
  };
}

function rigActivityViewModelToConfig(
  viewModel: RigActivityConfigViewModel
): RigActivityConfig {
  const quickActionStages = actionsViewModelToConfig(viewModel.actions);

  return {
    activity: viewModel.activity,
    constraints: viewModel.constraints,
    quickActionStages,
    ongoingActions: [],
    quickActionTime: secondsToDuration(viewModel.quickActionTimeSec),
  };
}

export function rigActivitiesConfigToViewModel(
  rigEffectiveConfig: RigEffectiveConfig,
  config: RigActivitiesConfiguration | undefined
): RigActivitiesFormViewModel {
  const activityMap: RigActivitiesFormViewModel["activities"] =
    Object.fromEntries(
      [
        AutonomousActivityType.ACTIVITY_WORKING_ROW,
        AutonomousActivityType.ACTIVITY_WORKING_HEADLAND,
        AutonomousActivityType.ACTIVITY_TRAVERSE,
        AutonomousActivityType.ACTIVITY_WORKING_DIVERT,
        AutonomousActivityType.ACTIVITY_COVERING_TRACKS,
        AutonomousActivityType.ACTIVITY_TRANSPORT,
        AutonomousActivityType.ACTIVITY_STARTUP,
        AutonomousActivityType.ACTIVITY_SHUTDOWN,
        AutonomousActivityType.ACTIVITY_PAUSE,
      ].map((activity) => [
        activity,
        defaultRigActivityConfigViewModel(activity),
      ])
    );

  for (const activityConfig of config?.activities ?? []) {
    activityMap[activityConfig.activity] =
      rigActivityConfigToViewModel(activityConfig);
  }

  return {
    rigEffectiveConfig,
    activities: activityMap,
  };
}

export function rigActivitiesViewModelToConfig(
  viewModel: RigActivitiesFormViewModel
): RigActivitiesConfiguration {
  return {
    activities: Object.values(viewModel.activities)
      .filter((activity) => activity.present)
      .map((activityVM) => rigActivityViewModelToConfig(activityVM)),
  };
}
